// eslint-disable-next-line no-restricted-imports
import { type NumberSchema } from "yup";

import { BTSelectItem } from "types/apiResponse/apiResponse";

import yup from "utilities/form/yup";

import {
    type IVarianceCodeExtraData,
    type IVarianceEntity,
    type VarianceFormValues,
} from "commonComponents/entity/variance/Variance/Variance.api.types";

import type { Int32BTServiceGroupedListItem, Int32BTServiceListItem } from "handlers/models";

import { RelatedCO } from "entity/purchaseOrder/PurchaseOrder/PurchaseOrder.api.types";

import type BillDefaultMockData from "../../../entity/bill/Bill/jsonExamples/Bill.default.api.json";
import type PurchaseOrderDefaultMockData from "../../../entity/purchaseOrder/PurchaseOrder/mockData/PurchaseOrderMockData.api.json";
import type PurchaseOrderMockData from "../../../entity/purchaseOrder/PurchaseOrder/PurchaseOrder.api.json";

export function varianceYupValidationSchema() {
    return yup.object().shape<VarianceFormValues>({
        isVariance: yup.boolean().label("Is Variance"),
        isEntirePoVariance: yup.boolean().label("Is Entire PO Variance"),
        hasLineItemCustomerVariance: yup.boolean().label("Is Line Item Customer Variance"),
        varianceCode: yup
            .number()
            .label("Variance Code")
            .when(
                ["isVariance", "isEntirePoVariance", "skipVarianceValidation"],
                (
                    isVariance: boolean,
                    isEntirePoVariance: boolean,
                    skipVarianceValidation: boolean,
                    varianceCode: NumberSchema,
                ) => {
                    return isVariance && isEntirePoVariance && !skipVarianceValidation
                        ? varianceCode.requiredDropdown().min(1, "Please select a variance code")
                        : varianceCode.notRequired();
                },
            )
            .nullable(),
        relatedPOs: yup.number().nullable().label("Related Purchase Orders"),
        relatedCO: yup.number().nullable().label("Related Change Orders"),
        relatedCOsToRemove: yup.array<number>().label("Related Change Orders to Remove"),
        skipVarianceValidation: yup.boolean(),
    });
}

export function calculateVariancePercent(variance: number, total: number) {
    if (variance > 0 && total > 0) {
        return (variance / total) * 100;
    } else if (variance > 0) {
        return 100;
    }
    return 0;
}

export const getVarianceForPurchaseOrders = (
    data: typeof PurchaseOrderMockData | typeof PurchaseOrderDefaultMockData,
): IVarianceEntity => {
    // Handle both grouped structure (newer Bills) and legacy structure (older Purchase Orders)
    let varianceCodesWithGroups: BTSelectItem<IVarianceCodeExtraData>[] = [];

    if (data.varianceCodes?.options) {
        // Newer grouped structure - preserve groups with children for UI rendering
        const options = data.varianceCodes.options as Int32BTServiceGroupedListItem[];
        varianceCodesWithGroups = options.flatMap(
            (group: Int32BTServiceGroupedListItem, index: number) => {
                const childItems =
                    group.options?.map(
                        (option: Int32BTServiceListItem) =>
                            new BTSelectItem<IVarianceCodeExtraData>(option),
                    ) ?? [];

                // Groups with null/empty name are ungrouped - render children directly
                if (!group.name) {
                    return childItems;
                }

                // Named groups get wrapped with children
                const groupItem = new BTSelectItem<IVarianceCodeExtraData>({
                    id: `group_${index}`,
                    name: group.name,
                });
                groupItem.children = childItems;
                return groupItem;
            },
        );
    } else if (data.varianceCodes?.value && Array.isArray(data.varianceCodes.value)) {
        // Legacy structure - direct array of items
        varianceCodesWithGroups = data.varianceCodes.value.map(
            (option) => new BTSelectItem<IVarianceCodeExtraData>(option as Int32BTServiceListItem),
        );
    }

    const relatedPOsWithGroups =
        data.relatedPOs?.value?.map((x) => new BTSelectItem(x)) ?? undefined;
    const relatedCOWithGroups = data.relatedCO?.value?.map((x) => new BTSelectItem(x)) ?? undefined;
    const relatedCOsList = (data.relatedCOsList as RelatedCO[])?.map((x) => new RelatedCO(x));

    return {
        isVariance: data.isVariance.value,
        isEntirePoVariance: data.isEntirePoVariance.value,
        varianceCodes: varianceCodesWithGroups,
        relatedPOs: relatedPOsWithGroups,
        relatedCO: relatedCOWithGroups,
        relatedCOsList: relatedCOsList,
        canViewChangeOrders: data.canViewChangeOrders ?? false,
        hasLinkedChangeOrder: data.hasLinkedChangeOrder ?? false,
        showAddFromChangeOrderButton: data.showAddFromChangeOrderButton ?? false,
        hasEligibleChangeOrdersToLink: data.hasEligibleChangeOrdersToLink ?? false,
        hasLineItemCustomerVariance: data.hasLineItemCustomerVariance ?? false,
    };
};

export const getVarianceForBills = (data: typeof BillDefaultMockData): IVarianceEntity => {
    // Preserve grouped structure (Builder variance + Client variance) with children for UI rendering
    // Render ungrouped items (null name) directly
    let varianceCodesWithGroups: BTSelectItem<IVarianceCodeExtraData>[] = [];
    if (data.varianceInformation.varianceCodes?.options) {
        varianceCodesWithGroups = data.varianceInformation.varianceCodes.options.flatMap(
            (group: Int32BTServiceGroupedListItem, index: number) => {
                const childItems =
                    group.options?.map(
                        (option: Int32BTServiceListItem) =>
                            new BTSelectItem<IVarianceCodeExtraData>(option),
                    ) ?? [];

                // Groups with null/empty name are ungrouped - render children directly
                if (!group.name) {
                    return childItems;
                }

                // Named groups get wrapped with children
                const groupItem = new BTSelectItem<IVarianceCodeExtraData>({
                    id: `group_${index}`,
                    name: group.name,
                });
                groupItem.children = childItems;
                return groupItem;
            },
        );
    }

    const relatedPOs: BTSelectItem[] | undefined =
        data.varianceInformation.relatedPurchaseOrder?.options?.[0]?.options?.map(
            (x) => new BTSelectItem(x),
        ) ?? undefined;

    let relatedCO: BTSelectItem[] | undefined = undefined;
    const relatedChangeOrderOptions = data.varianceInformation.relatedChangeOrder?.options as
        | Int32BTServiceGroupedListItem[]
        | undefined;
    if (relatedChangeOrderOptions && relatedChangeOrderOptions[0].options) {
        relatedCO = relatedChangeOrderOptions[0].options.map(
            (x: Int32BTServiceListItem) => new BTSelectItem(x),
        );
    }

    return {
        isVariance: data.varianceInformation.isVariance.value,
        isEntirePoVariance: data.varianceInformation.isEntireBillVariance.value,
        varianceCodes: varianceCodesWithGroups,
        relatedPOs: relatedPOs,
        relatedCO: relatedCO,
        canViewChangeOrders: data.userPermissions?.canViewChangeOrders ?? false,
        hasLinkedChangeOrder: false,
        hasLineItemCustomerVariance: data.varianceInformation.hasLineItemCustomerVariance,
        isReadOnly: data.varianceInformation.isReadOnly ?? false,
    };
};
